import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import SidebarDropdownLinks from '../SidebarDropdownLinks';
import { Link, useLocation } from 'react-router';
import { createNavLinks } from './mocks';
// import NotificationBellIcon from '../../assets/icons/notification-bell-icon.svg?react';
import SunIcon from '../../assets/icons/sun-icon.svg?react';
import MoonIcon from '../../assets/icons/moon-icon.svg?react';
import LogoutIcon from '../../assets/icons/log-out.svg?react';
import SidebarCrossIcon from '../../assets/icons/sidebar-cross-icon.svg?react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import { logout } from '../../actions/useAction';
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks';
import { showBodyScroll } from '../../utils/bodyScroll';
import { hideBodyScroll } from '../../utils/bodyScroll';
import { UserWithOrgContext } from '../../types/user';

interface SidebarProps {
  otherClasses?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ otherClasses }) => {
  const location = useLocation();
  const isSmallDevice = useMediaQuery('only screen and (max-width : 1024px)');
  const [toggle, setToggle] = useState('');
  const [userInfo, setUserInfo] = useLocalStorage<UserWithOrgContext | null>(
    'userInfo',
    null,
  );
  const navLinks = createNavLinks(userInfo);
  useEffect(() => {
    const findActiveRootTitle = () => {
      const directMatch = navLinks.find(
        (item) =>
          location.pathname === item.link ||
          location.pathname.startsWith(item.link + '/'),
      );
      if (directMatch) return directMatch.title;
      for (const item of navLinks) {
        if (item.links) {
          const nestedMatch = item.links.find(
            (link) =>
              location.pathname === link.link ||
              location.pathname.startsWith(link.link + '/'),
          );
          if (nestedMatch) return item.title;
        }
      }
      return '';
    };
    setToggle(findActiveRootTitle());
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleClick = () => {
    if (localStorage.theme === 'dark') {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    } else {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    }
  };

  useEffect(() => {
    if (
      localStorage.theme === 'dark' ||
      (!('theme' in localStorage) &&
        window.matchMedia('(prefers-color-scheme: dark)').matches)
    ) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  }, []);

  const dispatch = useDispatch();
  const { active } = useSelector(
    ({ switchVal }: { switchVal: { active: boolean } }) => switchVal,
  );

  const modalName = useSelector(
    (state: { modalToggle: { modalName: string } }) =>
      state.modalToggle.modalName,
  );

  useEffect(() => {
    if (isSmallDevice) {
      if (active) {
        hideBodyScroll();
      } else {
        showBodyScroll();
      }
    } else {
      showBodyScroll();
    }
  }, [active]);

  useEffect(() => {
    if (modalName !== '') {
      hideBodyScroll();
    } else {
      showBodyScroll();
    }
  }, [modalName]);

  const handleLogout = async () => {
    await dispatch(logout(setUserInfo) as any);
    if (window.innerWidth < 546) {
      dispatch(toggleSwitch());
    }
  };

  const sidebarClasses = clsx(
    otherClasses,
    'w-310 fixed top-0 z-10 h-dvh bg-neutral-tones-sidebar px-5 py-25 flex flex-col justify-between overflow-auto global-scrollbar-style gap-20 global-transition',
    active ? 'left-0 z-20' : '-left-310 lg:left-0',
  );

  const userFullName = userInfo?.user?.fullName || 'User';
  const userRoleName = userInfo?.user?.role?.name || '';
  const orgUsername= userInfo?.orgContext?.fullName || '';  

  return (
    <aside className={sidebarClasses} data-testid="sidebar">
      <div>
        <div className="mb-9 flex items-center justify-between">
          <Link to="/dashboard">
            <img
              src="/assets/images/logo.png"
              alt="logo"
              className="h-14 w-11"
            />
          </Link>
          {/* <button className="relative hidden cursor-pointer lg:block">
            <span className="bg-brand-fair-orange font-inter-variable -tracking-form-input absolute -top-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-full text-base leading-5 font-normal text-white">
              1
            </span>
            <NotificationBellIcon />
          </button> */}
          <button
            onClick={() => dispatch(toggleSwitch())}
            className="cursor-pointer lg:hidden"
          >
            <SidebarCrossIcon />
          </button>
        </div>

        <div className="mb-25 flex items-center justify-between lg:mb-12">
          <div>
            <h4 className="font-semibold capitalize">Hi, {orgUsername ? orgUsername :userFullName}</h4>
            <span className="font-inter-variable text-brand-fair-orange text-form-label font-bold uppercase lg:text-sm">
              {userRoleName}
            </span>
          </div>
          {/* <button className="relative cursor-pointer lg:hidden">
            <span className="bg-brand-fair-orange font-inter-variable -tracking-form-input absolute -top-1 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[10px] leading-3 font-normal text-white">
              1
            </span>
            <NotificationBellIcon className="h-8 w-8" />
          </button> */}
        </div>
        <nav className="flex flex-col gap-5">
          {navLinks?.map((node, index) => (
            <SidebarDropdownLinks
              {...node}
              key={index}
              setToggle={setToggle}
              toggle={toggle}
            />
          ))}
        </nav>
      </div>
      <div>
        <div className="flex items-center justify-between">
          {/* <span className="font-inter-variable text-neutral-tones-text/35 text-xs font-bold">
            © 2025, EHS Navigator.
          </span> */}
          <button
            onClick={() => handleClick()}
            className="border-neutral-tones-text/10 bg-card-backgrounds/5 relative h-8 w-16 cursor-pointer rounded-full border"
          >
            <MoonIcon className="dark:[&>path]:stroke-neutral-tones-text absolute top-2/4 left-1 -translate-y-2/4 [&>path]:transition-all [&>path]:duration-300" />
            <span className="bg-brand-byzantine global-transition absolute top-2/4 right-auto left-1 z-10 block h-22 w-22 -translate-y-2/4 rounded-full dark:left-9"></span>
            <SunIcon className="dark:[&>path]:fill-neutral-tones-text absolute top-2/4 right-1 -translate-y-2/4 [&>path]:transition-all [&>path]:duration-300" />
          </button>
          <button
            onClick={handleLogout}
            className={clsx(
              'font-inter-variable global-transition text-brand-byzantine flex w-fit cursor-pointer items-center !gap-1 rounded text-left text-base leading-6 font-medium',
            )}
          >
            <LogoutIcon className="svg-path-current-color" />
            Logout
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
